// src/pages/BeginTest.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import theme from "../theme";
import api from "../services/api";
import { toast } from "react-toastify";
import { Row, Col } from "antd";
import BackButton from "../components/BackButton";
import { useUser } from "../contexts/UserContext";

interface TestAssignment {
  id: number;
  test_id: number;
  test_name: string;
  industry_name: string;
  time_limit: number;
  passing_grade: number;
  question_count: number;
  completed_at: string | null;
  started_at: string | null;
  score: number | null;
  description: string;
}

const BeginTest: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: userLoading, isExternalApiUser, logout } = useUser();
  const [testAssignment, setTestAssignment] = useState<TestAssignment | null>(null);
  const [testAssignments, setTestAssignments] = useState<TestAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMultipleTests, setShowMultipleTests] = useState(false);
  // Latch the view into a session-ended state once Close tab is clicked, so
  // logout()'s setUser(null) can't flip isExternalApiUser() back to false
  // and re-render the wrong UI before window.close() (often) silently fails.
  const [sessionEnded, setSessionEnded] = useState(false);

  const handleCloseTab = async () => {
    setSessionEnded(true);
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch { /* ignore */ }
    }
    try { await logout(); } catch { /* ignore */ }
    window.close();
  };

  const formatTime = (time: string) => {
    let limit = "";

    if (time.trim() === "") {
      limit = "No"
    } else {
      limit = time + ":00"
    }

    limit += " time limit"
    return limit;
  }

  const loadTestAssignment = useCallback(async () => {
    // Wait for user info to load before deciding what to fetch — the
    // external-user gate below depends on it.
    if (userLoading) return;

    try {
      const assignmentId = searchParams.get('assignment_id');
      const testId = searchParams.get('test_id'); // Keep for backward compatibility
      const external = user?.is_external_api_user || false;

      // External (B2B) candidates can only access /begin-test via the magic
      // link, which always supplies assignment_id. If they end up here without
      // one (manual URL entry, logo click, refresh after the URL was cleared),
      // show a clear message instead of leaking the multi-assessment selector
      // or a "Go to Login" page they can't actually use.
      if (external && !assignmentId) {
        setError("This page is only accessible via the magic link sent by the recruiter or employer who invited you. Please use that link to start your assessment.");
        setLoading(false);
        return;
      }

      if (assignmentId) {
        // Load specific assignment by ID (preferred method)
        const response = await api.get(`user/test-assignment/?assignment_id=${assignmentId}`);
        const assignment = response.data;
        // Check if test is already completed
        if (assignment.completed_at) {
          setError(`You have already completed this assessment on ${new Date(assignment.completed_at).toLocaleDateString()}. Your score was ${assignment.score}%.`);
        } else {
          setTestAssignment(assignment);
        }
      } else if (testId) {
        // Load specific test assignment by test_id (backward compatibility)
        const response = await api.get(`user/test-assignment/?test_id=${testId}`);
        const assignment = response.data;
        // Check if test is already completed
        if (assignment.completed_at) {
          setError(`You have already completed this assessment on ${new Date(assignment.completed_at).toLocaleDateString()}. Your score was ${assignment.score}%.`);
        } else {
          setTestAssignment(assignment);
        }
      } else {
        console.log('2')
        // Load all test assignments
        const response = await api.get('user/test-assignment/');
        const assignments = response.data;

        if (assignments.length === 1) {
          // Only one test, proceed directly
          const assignment = assignments[0];
          if (assignment.completed_at) {
            setError(`You have already completed this assessment on ${new Date(assignment.completed_at).toLocaleDateString()}. Your score was ${assignment.score}%.`);
          } else {
            setTestAssignment(assignment);
          }
        } else if (assignments.length > 1) {
          console.log(assignments);
          // Multiple tests, show selection
          setTestAssignments(assignments);
          setShowMultipleTests(true);
        }
      }
    } catch (err: any) {
      console.error('Failed to load test assignment:', err);
      if (err.response?.status === 401) {
        toast.error('Please log in to access your assessment');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No active assessment found. Please contact your administrator.');
      } else {
        setError('Failed to load assessment information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [searchParams, navigate, user, userLoading]);

  useEffect(() => {
    loadTestAssignment();
  }, [loadTestAssignment]);

  const handleStartTest = async () => {
    if (!testAssignment) return;

    // Request fullscreen before entering the test — must be called from a user gesture
    try {
      await document.documentElement.requestFullscreen();
    } catch {
      // Fullscreen denied or unsupported — proceed anyway, exit will be logged by useCheatDetection
    }

    navigate(`/candidate/question?assignment_id=${testAssignment.id}`, {
      state: { assignmentId: testAssignment.id }
    });
  };

  if (loading) {
    return (
      <div style={container}>
        <div style={wrapper}>
          <div style={card}>
            <p>Loading assessment information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (sessionEnded) {
    return (
      <div style={container}>
        <div style={wrapper}>
          <div style={card}>
            <h1 style={title}>Session ended</h1>
            <p style={paragraph}>
              You can now close this tab. If your browser didn't close it
              automatically, please close it manually.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleTestSelection = (testId: number) => {
    navigate(`/begin-test?test_id=${testId}`);
  };

  if (error) {
    return (
      <div style={container}>
        <div style={wrapper}>
          <div style={card}>
            <h1 style={title}>Assessment Not Available</h1>
            <p style={paragraph}>
              {error}
            </p>
            {isExternalApiUser() ? (
              <button style={button} onClick={handleCloseTab}>
                Close tab
              </button>
            ) : (
              <button style={button} onClick={() => navigate('/login')}>
                Go to Login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (showMultipleTests) {
    return (
      <div style={container}>
        <div style={wrapper}>
          <div style={card}>
            <h1 style={title}>Select Assessment</h1>
            <p style={paragraph}>
              You have multiple assessments assigned. Please select which assessment you would like to take:
            </p>
            <div style={testListContainer}>
              {testAssignments.map((assignment) => (
                <div key={assignment.id} style={testItem}>
                  <h3 style={testTitle}>{assignment.test_name}</h3>
                  <p style={testDetails}>
                    {assignment.industry_name} • {assignment.question_count} questions • {assignment.time_limit} minutes
                  </p>
                  <button
                    style={selectButton}
                    onClick={() => handleTestSelection(assignment.test_id)}
                  >
                    Start This Assessment
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!testAssignment) {
    return (
      <div style={container}>
        <div style={wrapper}>
          <div style={card}>
            <h1 style={title}>Assessment Not Available</h1>
            <p style={paragraph}>
              No assessment found.
            </p>
            {isExternalApiUser() ? (
              <button style={button} onClick={handleCloseTab}>
                Close tab
              </button>
            ) : (
              <button style={button} onClick={() => navigate('/login')}>
                Go to Login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isExternalApiUser() && <BackButton />}
      <div style={container}>
        <Row style={rowStyle} gutter={[40, 32]}>
          <Col xs={24} md={12} style={colStyle}>
            <div style={{ ...card, ...wrapper, ...details }}>
              <h4 style={heading}>{testAssignment.test_name}</h4>
              <div style={metaContainer}>
                <span style={metaText}>{testAssignment.question_count} {testAssignment.question_count === 1 ? 'question' : 'questions'}</span>
                <span style={metaText}>{formatTime(testAssignment.time_limit.toString())}</span>
              </div>
              <div style={about}>About this assessment:</div>
              <p>{testAssignment.description}</p>
            </div>
          </Col>

          <Col xs={24} md={12} style={colStyle}>
            <div style={{ ...card, ...wrapper, alignItems: "stretch", textAlign: "center" }}>
              <p style={paragraph}>
                {testAssignment.started_at
                  ? "You have already started this assessment. Click the button below to resume where you left off."
                  : isExternalApiUser()
                    ? "When you start the assessment, a counter will display your remaining time. Once you finish, your results will be sent directly to the recruiter or employer who invited you."
                    : "When you start the assessment, a counter will display your remaining time. At the end of the assessment, your results will be calculated and broken down for you. You will also receive a copy via email."}
              </p>

              <p>
                This assessment has the following anti-cheat mechanisms:
                <br /><br />
                <ul style={list}>
                  <li>Copy-paste disabled</li>
                  <li>Right-click disabled</li>
                  <li>Screenshot detection</li>
                  <li>Screen recording blocked</li>
                  <li>Full-screen required</li>
                  <li>ChatGPT/LLM detection</li>
                  <li>Multi-monitor prevention</li>
                </ul>
              </p>

              <p style={{ ...paragraph, ...disclaimer }}>
                Keep in mind that if you run out of time or close the assessment window, you
                will not be able to return later. Please allow yourself adequate time
                to complete the assessment.
              </p>

              <p style={paragraph}>
                The assessment will begin when you click the button below.
              </p>

              <h1 style={title}>{testAssignment.started_at ? "Resume?" : "Ready?"}</h1>

              <button
                style={{ ...button, ...testStart }}
                onClick={handleStartTest}
              >
                {testAssignment.started_at ? "Resume the assessment" : "Start the assessment"}
              </button>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};


const container: React.CSSProperties = {
  width: "100%",
  maxWidth: 1100,
  margin: "0 auto",
  paddingTop: "3rem",
  paddingBottom: "3rem",
};

const card: React.CSSProperties = {
  width: "100%",
  padding: "3rem 2rem 3.5rem",
  background: "#ffffff",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
};

const wrapper: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const title: React.CSSProperties = {
  margin: 0,
  color: theme.colors.primary,
  fontSize: 36,
  fontWeight: 700,
};

const paragraph: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#000",
  marginTop: "1.25rem",
};

const button: React.CSSProperties = {
  marginTop: "2rem",
  padding: "0.9rem 1rem",
  width: "100%",
  borderRadius: 9999,
  border: "none",
  background: theme.colors.primary,
  color: "#fff",
  fontSize: "1rem",
  cursor: "pointer",
  fontWeight: 600,
};

const testListContainer: React.CSSProperties = {
  marginTop: "1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};

const testItem: React.CSSProperties = {
  padding: "1rem",
  border: "1px solid #e0e0e0",
  borderRadius: 8,
  backgroundColor: "#f9f9f9",
};

const testTitle: React.CSSProperties = {
  margin: "0 0 0.5rem 0",
  color: theme.colors.primary,
  fontSize: "1.1rem",
  fontWeight: 600,
};

const testDetails: React.CSSProperties = {
  margin: "0 0 1rem 0",
  fontSize: "0.9rem",
  color: "#666",
};

const selectButton: React.CSSProperties = {
  padding: "0.7rem 1.5rem",
  borderRadius: 9999,
  border: "none",
  background: theme.colors.primary,
  color: "#fff",
  fontSize: "0.9rem",
  cursor: "pointer",
  fontWeight: 600,
};

const disclaimer: React.CSSProperties = {
  color: "#F80000"
}

const testStart: React.CSSProperties = {
  background: "#0051FF",
  height: "38px",
  padding: 0
}

const list: React.CSSProperties = {
  listStylePosition: "inside",
  textAlign: "left",
  margin: "0 auto",
  display: "inline-block",
  fontWeight: "bold"
}

const colStyle: React.CSSProperties = {
  display: "flex",
}

const about: React.CSSProperties = {
  fontWeight: 'bold'
}

const details: React.CSSProperties = {
  textAlign: "left",
  alignItems: "flex-start",
  gap: "1rem"
}

const metaContainer: React.CSSProperties = {
  display: "flex",
  gap: "0px 7px",
  flexWrap: "wrap"
};

const metaText: React.CSSProperties = {
  fontSize: 12,
  color: "#666",
  fontWeight: 500,
  border: "1px solid #E6EBFA",
  padding: "0px 15px",
  borderRadius: "5px"
};

const rowStyle: React.CSSProperties = {
  padding: "0px 0px 30px 0px",
}

const heading: React.CSSProperties = {
  color: "#0051FF",
  fontWeight: "bold"
}


export default BeginTest;
